var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
import { createElement, forwardRef, useImperativeHandle, useRef, useEffect } from 'react';
import { dashToPascalCase, kebabCase, isCustomEvent, attachCustomEvents, detachCustomEvents } from './utils';
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export function createReactComponent({ tagName, defineCustomElement, }) {
    if (defineCustomElement !== undefined) {
        defineCustomElement();
    }
    const displayName = dashToPascalCase(tagName);
    const ReactFunctionalComponent = forwardRef((_a, ref) => {
        var { className, children } = _a, rest = __rest(_a, ["className", "children"]);
        const element = useRef();
        useImperativeHandle(ref, () => element.current);
        useEffect(() => {
            element.current && attachCustomEvents(element.current, rest);
            return () => {
                element.current && detachCustomEvents(element.current);
            };
        });
        const propsToPass = Object.entries(rest).reduce((acc, [propName, propValue]) => {
            // Don't transform properties for event listeners
            if (propName.startsWith('on') && propName[2] === propName[2].toUpperCase()) {
                const eventName = propName.substring(2).toLowerCase();
                if (typeof document !== 'undefined' && !isCustomEvent(eventName)) {
                    return Object.assign(Object.assign({}, acc), { [propName]: propValue });
                }
            }
            return Object.assign(Object.assign({}, acc), { [kebabCase(propName)]: propValue });
        }, {});
        /**
         * We use createElement here instead of
         * React.createElement to work around a
         * bug in Vite (https://github.com/vitejs/vite/issues/6104).
         * React.createElement causes all elements to be rendered
         * as <tagname> instead of the actual Web Component.
         */
        return createElement(tagName, Object.assign({ ref: element, class: className }, propsToPass), children);
    });
    ReactFunctionalComponent.displayName = displayName;
    return ReactFunctionalComponent;
}
